/**
 * sl-ya4v.6: useWorkspacePublicHost — non-suspense lookup of the workspace
 * public-host snapshot (default + branded + custom + effective).
 *
 * Replaces the legacy `useWorkspaceSlug` hook (sl-bpu6.2.1). This returns the
 * workspace host snapshot for settings and default-host fallbacks. Public
 * view URLs must be resolved through `useCustomAppPublicUrl`, because
 * custom/branded domain rows are bound to a specific CustomApp.
 * sl-ya4v.8: scheme is backend-decided (http for dev `*.lvh.me`, https
 * otherwise) — FE MUST consume URL fields directly and NEVER concat
 * `https://${host}` on the client.
 *
 * Pages that need the host *only* for optional UI (e.g. copy-public-URL) must
 * not suspend the whole route on this query — the list page has to stay
 * rendered even if the host fetch fails or is slow. Returns the full SWR
 * state struct so callers can distinguish "still loading" from "settled to
 * null / failed".
 */
import { GraphQLService } from '@affine/core/modules/cloud';
import { onBackendAvailable } from '@affine/core/utils/backend-availability';
import {
  type WorkspacePublicHostQuery,
  workspacePublicHostQuery,
} from '@affine/graphql';
import { useService } from '@toeverything/infra';
import type { GraphQLError } from 'graphql';
import { useEffect } from 'react';
import useSWR from 'swr';

export interface UseWorkspacePublicHostResult {
  defaultHost: string | null;
  brandedHost: string | null;
  customDomain: string | null;
  /** Same precedence as backend `getEffectiveHost`: custom > branded > default. */
  effectiveHost: string | null;
  /**
   * sl-ya4v.8: Full URLs (scheme + host). Frontend MUST consume these for
   * every clickable link / share-URL — never concat `https://${host}`. Scheme
   * is dev-aware (http for *.lvh.me / *.localhost, https otherwise).
   */
  defaultUrl: string | null;
  brandedUrl: string | null;
  customDomainUrl: string | null;
  effectiveUrl: string | null;
  isLoading: boolean;
  error: GraphQLError | undefined;
}

type HostSnapshot = WorkspacePublicHostQuery['workspacePublicHost'];

export function useWorkspacePublicHost(
  workspaceId: string | null
): UseWorkspacePublicHostResult {
  const graphql = useService(GraphQLService);

  const { data, isLoading, error, mutate } = useSWR<
    HostSnapshot | null,
    GraphQLError
  >(
    workspaceId ? ['workspacePublicHost', workspaceId] : null,
    async () => {
      const res = await graphql.gql({
        query: workspacePublicHostQuery,
        variables: { workspaceId: workspaceId as string },
      });
      return res.workspacePublicHost ?? null;
    },
    // Public-host availability can change while the app is open (plan
    // activation, branded host or custom-domain lifecycle). A normal SWR
    // resource revalidates when the consumer remounts; immutable caching can
    // otherwise retain an early null snapshot and disable publishing forever.
    // Global SWRConfig sets suspense:true — opt-out so consumers can render
    // their own loading/error UI instead of suspending the route.
    { suspense: false }
  );

  // Network errors deliberately do not retry globally. Resume this resource
  // through the canonical recovery event, without a separate polling loop.
  useEffect(() => {
    if (!workspaceId) return;
    return onBackendAvailable(() => {
      void mutate();
    });
  }, [mutate, workspaceId]);

  return {
    defaultHost: data?.defaultHost ?? null,
    brandedHost: data?.brandedHost ?? null,
    customDomain: data?.customDomain ?? null,
    effectiveHost: data?.effectiveHost ?? null,
    defaultUrl: data?.defaultUrl ?? null,
    brandedUrl: data?.brandedUrl ?? null,
    customDomainUrl: data?.customDomainUrl ?? null,
    effectiveUrl: data?.effectiveUrl ?? null,
    isLoading,
    error,
  };
}
